/**
 * The Wiki's URL space in one place: the router (pages/WikiV2.jsx), the
 * TopBar crumb and every section's links read routes from here, so a route
 * cannot mean one thing to the page and another to the breadcrumb.
 *
 * Pure (no react-router import) so shell code and tests can use it freely.
 */

export const WIKI_BRAIN_TABS = Object.freeze(['tree', 'review', 'insights', 'settings']);

const enc = (id) => encodeURIComponent(String(id));

/** URL builders. Always use these instead of hand-built strings. */
export const wikiPaths = Object.freeze({
    home: () => '/wiki',
    ack: () => '/wiki/acknowledge',
    ask: () => '/wiki/ask',
    category: (id) => `/wiki/c/${enc(id)}`,
    page: (id) => `/wiki/p/${enc(id)}`,
    pageEdit: (id) => `/wiki/p/${enc(id)}/edit`,
    archived: () => '/wiki/archived',
    brain: (tab = 'tree') => (tab && tab !== 'tree' ? `/wiki/brain/${tab}` : '/wiki/brain'),
});

function decode(segment) {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
}

function searchParams(search) {
    try {
        return new URLSearchParams(search || '');
    } catch {
        return new URLSearchParams();
    }
}

const HOME_REDIRECT = Object.freeze({ view: 'redirect', to: '/wiki' });

/**
 * Parse a pathname (+ optional search) into a Wiki route, or `null` when the
 * path is not under /wiki.
 *
 *   /wiki                         { view: 'home' }
 *   /wiki/acknowledge             { view: 'ack' }
 *   /wiki/ask                     { view: 'ask' }
 *   /wiki/archived                { view: 'archived' }       (#872, editors and admins)
 *   /wiki/c/:id                   { view: 'category', id }   (category OR subcategory)
 *   /wiki/p/:id                   { view: 'page', id, mode: 'read' }
 *   /wiki/p/:id/edit              { view: 'page', id, mode: 'edit' }
 *   /wiki/brain[/:tab]            { view: 'brain', tab }     tab ∈ tree|review|insights|settings
 *   /wiki/p/:id/history           { view: 'redirect', to: '/wiki/p/:id' }   (versions live in the page rail)
 *   /wiki/new[?category=&subcategory=&mode=import]
 *                                 { view: 'redirect', to: '/wiki', modal: { kind: 'page', opts } }
 *   /wiki/c/new[?parent=]         { view: 'redirect', to: '/wiki', modal: { kind: 'category'|'subcategory', opts } }
 *   trailing slash                { view: 'redirect', to: <same path without it> }
 *   anything else under /wiki     { view: 'redirect', to: '/wiki' } (unknown brain tab → '/wiki/brain')
 */
export function parseWikiPath(pathname, search = '') {
    const path = String(pathname || '');
    if (!/^\/wiki(\/|$)/.test(path)) return null;
    if (path.length > '/wiki'.length && path.endsWith('/')) {
        const trimmed = path.replace(/\/+$/, '');
        return { view: 'redirect', to: `${trimmed}${search || ''}` };
    }
    const seg = path.slice('/wiki'.length).split('/').filter(Boolean).map(decode);
    if (seg.length === 0) return { view: 'home' };

    const [head, a, b] = seg;
    switch (head) {
    case 'acknowledge':
        return seg.length === 1 ? { view: 'ack' } : HOME_REDIRECT;
    case 'ask':
        return seg.length === 1 ? { view: 'ask' } : HOME_REDIRECT;
    case 'archived':
        return seg.length === 1 ? { view: 'archived' } : HOME_REDIRECT;
    case 'new': {
        if (seg.length !== 1) return HOME_REDIRECT;
        const params = searchParams(search);
        const opts = { mode: params.get('mode') === 'import' ? 'import' : 'write' };
        if (params.get('category')) opts.categoryId = params.get('category');
        if (params.get('subcategory')) opts.subcategoryId = params.get('subcategory');
        return { view: 'redirect', to: '/wiki', modal: { kind: 'page', opts } };
    }
    case 'c':
        if (seg.length !== 2) return HOME_REDIRECT;
        if (a === 'new') {
            const parentId = searchParams(search).get('parent');
            return parentId
                ? { view: 'redirect', to: '/wiki', modal: { kind: 'subcategory', opts: { parentId } } }
                : { view: 'redirect', to: '/wiki', modal: { kind: 'category', opts: {} } };
        }
        return { view: 'category', id: a };
    case 'p':
        if (seg.length === 2) return { view: 'page', id: a, mode: 'read' };
        if (seg.length === 3 && b === 'edit') return { view: 'page', id: a, mode: 'edit' };
        if (seg.length === 3 && b === 'history') return { view: 'redirect', to: wikiPaths.page(a) };
        return HOME_REDIRECT;
    case 'brain':
        if (seg.length === 1) return { view: 'brain', tab: 'tree' };
        if (seg.length === 2 && WIKI_BRAIN_TABS.includes(a) && a !== 'tree') return { view: 'brain', tab: a };
        return { view: 'redirect', to: '/wiki/brain' };
    default:
        return HOME_REDIRECT;
    }
}

export function isWikiPath(pathname) {
    return /^\/wiki(\/|$)/.test(String(pathname || ''));
}
